import { z } from "zod";

/**
 * Lab validation schemas
 * Backend-aligned: LabCreate only accepts lab_name + is_external (additionalProperties: false)
 */

export const labNameSchema = z
  .string()
  .min(2, "Lab name must be at least 2 characters long")
  .max(255, "Lab name must be less than 255 characters")
  .regex(/^[a-zA-Z0-9\s\-',.()&]+$/, "Lab name contains invalid characters")
  .trim();

/**
 * Schema for creating a new lab (backend-aligned)
 * Only lab_name and is_external are accepted by the backend
 */
export const labCreateSchema = z.object({
  lab_name: labNameSchema,
  is_external: z.boolean().optional().default(false),
});

/**
 * Schema for updating a lab (all fields optional for partial update)
 */
export const labUpdateSchema = z
  .object({
    lab_name: labNameSchema,
    is_external: z.boolean(),
  })
  .partial();

/**
 * Schema for deleting lab(s)
 * Backend expects field name "uuid", not "lab_ids"
 */
export const labDeleteSchema = z.object({
  uuid: z
    .array(z.string().uuid("Invalid lab ID"))
    .min(1, "Must provide at least one lab ID"),
});

/**
 * Schema for lab filters (for search/filter forms)
 */
export const labFiltersSchema = z.object({
  search: z.string().optional(),
  created_at_from: z.string().optional(),
  created_at_to: z.string().optional(),
  sort_by: z.enum(["lab_name", "created_at", "updated_at"]).optional(),
  sort_order: z.enum(["asc", "desc"]).optional(),
});

/**
 * Export inferred types for use in components
 */
export type LabCreateForm = z.infer<typeof labCreateSchema>;
export type LabUpdateForm = z.infer<typeof labUpdateSchema>;
export type LabDeleteForm = z.infer<typeof labDeleteSchema>;
export type LabFiltersForm = z.infer<typeof labFiltersSchema>;

// Aliases for actions
export const createLabSchema = labCreateSchema;
export const updateLabSchema = labUpdateSchema;
export const deleteLabSchema = labDeleteSchema;

/**
 * Lab-Location relationship schemas
 */
export const labLocationAssignSchema = z.object({
  lab_ids: z
    .array(z.string().uuid("Invalid lab ID"))
    .min(1, "Must provide at least one lab ID"),
  location_ids: z
    .array(z.string().uuid("Invalid location ID"))
    .min(1, "Must provide at least one location ID"),
});

export const labLocationRemoveSchema = z.object({
  lab_ids: z
    .array(z.string().uuid("Invalid lab ID"))
    .min(1, "Must provide at least one lab ID"),
  location_ids: z
    .array(z.string().uuid("Invalid location ID"))
    .min(1, "Must provide at least one location ID"),
});

export type LabLocationAssignForm = z.infer<typeof labLocationAssignSchema>;
export type LabLocationRemoveForm = z.infer<typeof labLocationRemoveSchema>;
